import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';

import { ClientsService } from '@intranet/modules/clients/clients.service';
import { ClientDashboardDataResult } from '@intranet/modules/clients/client.types';

type ClientDashboardSummaryView = 'currentMonth' | 'ytd';

/**
 * Small summary panel shown on the client dashboard, projected into the
 * heading of the client/group's tab-nav (next to the client name). Two tabs
 * (Current Month / YTD) switch between two sets of financial totals for the
 * client(s) passed in.
 */
@Component({
    selector: 'client-dashboard-summary',
    templateUrl: './client-dashboard-summary.component.html',
    styleUrls: ['./client-dashboard-summary.component.scss'],
})
export class ClientDashboardSummaryComponent implements OnChanges {
    @Input() clientIds: number[] = [];
    // Slim single-line layout used when this panel sits inline in the tab-nav
    // heading, where there isn't room for the full centred/wrapping card.
    @Input() compact: boolean = false;

    activeView: ClientDashboardSummaryView = 'currentMonth';
    data: ClientDashboardDataResult | null = null;
    isLoading = false;
    error: string | null = null;

    constructor(private clientsService: ClientsService) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes['clientIds']) {
            return;
        }
        // Guard against a bound expression that returns a fresh array
        // reference on every change-detection cycle (e.g. a method call in
        // the template) - only refetch when the actual set of ids changed,
        // not just the array's identity. Without this, a parent binding
        // mistake turns into a runaway flood of requests to the server.
        const previousIds = (changes['clientIds'].previousValue || []) as number[];
        const currentIds = (changes['clientIds'].currentValue || []) as number[];
        if (!changes['clientIds'].firstChange && this.sameIds(previousIds, currentIds)) {
            return;
        }
        this.loadData();
    }

    private sameIds(a: number[], b: number[]): boolean {
        if (a.length !== b.length) {
            return false;
        }
        const sortedA = [...a].sort((x, y) => x - y);
        const sortedB = [...b].sort((x, y) => x - y);
        return sortedA.every((value, index) => value === sortedB[index]);
    }

    onTabChange(view: string): void {
        this.activeView = view as ClientDashboardSummaryView;
    }

    private loadData(): void {
        if (!this.clientIds || this.clientIds.length === 0) {
            this.data = null;
            return;
        }

        this.isLoading = true;
        this.error = null;

        this.clientsService.getClientDashboardData(this.clientIds).subscribe({
            next: (result) => {
                this.isLoading = false;
                if (result && result.success) {
                    this.data = result;
                } else {
                    this.data = null;
                    this.error = (result && result.error) || 'Unable to load summary data.';
                }
            },
            error: () => {
                this.isLoading = false;
                this.data = null;
                this.error = 'Unable to load summary data.';
            },
        });
    }
}
